/**
 * followupRecommendationEngine.js
 * Entity-specific pastoral follow-up recommendations.
 *
 * Unlike the workflow followup engine (which only sees active workflows),
 * this module crosses relationship memory + workflow state to recommend
 * specific follow-up actions for known entities.
 *
 * Pastoral language — never CRM, never analytics.
 */

import { getAllEntityHistories }   from "../kevia-relationships/relationalMemoryEngine";
import { computeFollowupItems }    from "../kevia-workflows/followupEngine";
import { getActiveWorkflows }      from "../kevia-workflows/workflowState";

const RISKY_TONES  = new Set(["followup_urgent", "sensitive"]);
const SIGNAL_LIMIT = 5;  // max recommendations

/**
 * @typedef {{
 *   entityName: string,
 *   entityType: string,
 *   label:      string,
 *   days:       number | null,
 *   urgency:    "high" | "medium" | "low",
 *   source:     "memory" | "workflow",
 * }} FollowupRecommendation
 */

/**
 * Build entity-specific follow-up recommendations.
 *
 * @returns {{ recommendations: FollowupRecommendation[], compact: string | null }}
 */
export function buildFollowupRecommendations() {
  const recommendations = [];

  // ── From relationship memory ───────────────────────────────────────────────
  const histories = getAllEntityHistories();
  for (const record of histories.slice(0, 15)) {
    const recent = record.signals?.[0];
    if (!recent || !RISKY_TONES.has(recent.tone)) continue;

    const days = recent.visitDays ?? null;
    const label =
      recent.tone === "sensitive"
        ? `Hay situaciones delicadas pendientes con ${record.name}.`
        : days != null
          ? `La ${record.entityType === "family" ? "familia" : "persona"} ${record.name} lleva ${days} días sin visita.`
          : `Sería bueno retomar el seguimiento con ${record.name}.`;

    recommendations.push({
      entityName: record.name,
      entityType: record.entityType,
      label,
      days,
      urgency:    recent.tone === "sensitive" ? "high" : "medium",
      source:     "memory",
    });

    if (recommendations.length >= SIGNAL_LIMIT) break;
  }

  // ── From workflow followups (for entities not already covered) ────────────
  const workflowFollowups = computeFollowupItems();
  const coveredNames = new Set(recommendations.map((r) => r.entityName));

  for (const f of workflowFollowups) {
    if (!f.entityName || coveredNames.has(f.entityName)) continue;

    recommendations.push({
      entityName: f.entityName,
      entityType: "unknown",
      label:      f.label,
      days:       f.daysSinceActivity,
      urgency:    f.kind === "overdue" ? "high" : "medium",
      source:     "workflow",
    });

    if (recommendations.length >= SIGNAL_LIMIT) break;
  }

  // Sort by urgency
  recommendations.sort((a, b) =>
    (a.urgency === "high" ? 0 : a.urgency === "medium" ? 1 : 2) -
    (b.urgency === "high" ? 0 : b.urgency === "medium" ? 1 : 2)
  );

  const compact = recommendations.length > 0
    ? recommendations[0].label
    : null;

  return { recommendations, compact };
}
